import React, { createContext, useCallback, useState, useContext } from 'react';
import jwtDecode, { JwtPayload } from 'jwt-decode';

import api from '../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  identification_document: string;
  cellphone_number: string;
  job_title: string;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

export interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@ISOPOINT:token');
    const user = localStorage.getItem('@ISOPOINT:user');

    if (token && user) {
      const { exp } = jwtDecode<JwtPayload>(token);

      if (exp && Date.now() <= exp * 1000) {
        api.defaults.headers.authorization = `Bearer ${token}`;
        return { token, user: JSON.parse(user) };
      }
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    const reseponse = await api.post('sessions', { email, password });

    const { token, user } = reseponse.data;

    localStorage.setItem('@ISOPOINT:token', token);
    localStorage.setItem('@ISOPOINT:user', JSON.stringify(user));

    api.defaults.headers.authorization = `Bearer ${token}`;

    setData({ token, user });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@ISOPOINT:token');
    localStorage.removeItem('@ISOPOINT:user');
    api.defaults.headers.authorization = '';
    window.location.href = window.location.origin;

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@ISOPOINT:user', JSON.stringify(user));

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  return (
    <AuthContext.Provider
      value={{ user: data.user, signIn, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
